import { UseFormReturn } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import type { IFormulaRequestCache } from '@utils/managers/IFormulaRequestCache';

/**
 * Абстракция вычисления формул для resolver-валидации.
 *
 * Изолирует `FormulaManager` (async + eval + сеть + ConfigService) от чистой
 * оркестрации валидации. Благодаря этому Layer 2 (`evaluateField`) тестируется
 * фейковым вычислителем, а реальный движок подключается одной адаптацией.
 */
export interface IFormulaEvaluator {
    /** Булева формула (visibility/required/readonly/workCalendar). Пустое правило → fallback. */
    evalBool(rule: string | undefined, fallback: boolean): Promise<boolean>;
    /** Значение-формула (границы min/max для дат). Пустое правило → undefined. */
    evalReplace(rule: string | undefined): Promise<string | undefined>;
}

/**
 * Реальный вычислитель на `FormulaManager`.
 *
 * Соответствует поведению хуков `useFormulaState` (EvalFormulaValues) и
 * `useFormulaReplace` (ReplaceFormulaValues): в режиме edit/new менеджер читает
 * ЖИВЫЕ значения через `formMethods.getValues`, поэтому видит значения всех
 * полей, включая поля размонтированных табов (при `shouldUnregister:false`).
 *
 * Инстансы `FormulaManager` кэшируются по тексту правила в пределах одного
 * вычислителя — менеджер парсит правило в `Init`, а значения читаются на момент
 * `Eval`, так что переиспользование безопасно.
 */
export function createFormulaManagerEvaluator(
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any> | undefined,
    isEdit: boolean,
    isNew: boolean,
    requestCache?: IFormulaRequestCache,
    onEvaluation?: () => void,
    onError?: (error: unknown) => void,
): IFormulaEvaluator {
    const cache = new Map<string, FormulaManager>();

    const getManager = (rule: string): FormulaManager => {
        let m = cache.get(rule);
        if (!m) {
            m = new FormulaManager(rule, undefined, requestCache);
            m.Init(fields, formMethods);
            cache.set(rule, m);
        }
        return m;
    };

    return {
        async evalBool(rule, fallback) {
            if (!rule) return fallback;
            onEvaluation?.();
            try {
                const result = await getManager(rule).EvalFormulaValues(isEdit, isNew);
                return result === undefined || result === null ? fallback : Boolean(result);
            } catch (error) {
                onError?.(error);
                console.error(error);
                return fallback;
            }
        },
        async evalReplace(rule) {
            if (!rule) return undefined;
            onEvaluation?.();
            try {
                // Паритет с useFormulaReplace: границы min/max дат подставляются в режиме (true, true),
                // иначе токены {isNew}/{isEdit}/{isView} внутри границы дали бы иное значение, чем в форме.
                return await getManager(rule).ReplaceFormulaValues(true, true, undefined, false);
            } catch (error) {
                onError?.(error);
                console.error(error);
                return undefined;
            }
        },
    };
}
